/**
 * Eigenes Konto: Stammangaben und Passwort.
 *
 * Das Zertifikat steht hier und nicht nur in der Verwaltung, weil es im
 * Bericht unter der Unterschrift erscheint – wer prüft, soll seine eigene
 * Nummer selbst richtigstellen können.
 */

import { useEffect, useState } from 'react'
import { useKalenderabgleich } from '../api/aufgaben'
import { ROLLENTEXT } from '../api/typen'
import { useEigenesAendern, usePasswortAendern } from '../api/verwaltung'
import { Fehlerhinweis, datumDeutsch } from '../bausteine/Hinweise'
import { Unterschriftfeld } from '../bausteine/Unterschriftfeld'
import { Zweitfaktor } from '../bausteine/Zweitfaktor'
import { useKonto } from '../konto/KontoKontext'

export function Konto() {
  const { konto } = useKonto()

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-bold">Mein Konto</h1>

      <div className="karte">
        <dl className="grid gap-x-4 gap-y-2 sm:grid-cols-[10rem_1fr]">
          <dt className="font-semibold text-slate-800">E-Mail</dt>
          <dd>{konto?.email}</dd>
          <dt className="font-semibold text-slate-800">Rolle</dt>
          <dd>{konto ? ROLLENTEXT[konto.rolle] : '—'}</dd>
          <dt className="font-semibold text-slate-800">Letzte Anmeldung</dt>
          <dd>{datumDeutsch(konto?.letzte_anmeldung)}</dd>
          <dt className="font-semibold text-slate-800">Kundenkreis</dt>
          <dd>
            {konto?.kunden_eingeschraenkt
              ? 'Auf zugeordnete Kunden beschränkt'
              : 'Alle Kunden'}
          </dd>
        </dl>
      </div>

      <Stammangaben />

      <Unterschriftsablage />

      <Kalenderabgleich />

      <Zweitfaktor pfad="/api/konto/zweitfaktor" />

      {konto?.hat_passwort ? (
        <Passwortwechsel />
      ) : (
        <div className="karte text-slate-700">
          <h2 className="text-xl font-bold text-slate-900">Passwort</h2>
          <p className="mt-2">
            Dieses Konto meldet sich über Microsoft 365 an und hat kein eigenes Passwort.
            Ein Wechsel ist deshalb nicht nötig.
          </p>
        </div>
      )}
    </div>
  )
}

function Stammangaben() {
  // Telefon und Zertifikat kommen aus /api/auth/ich. Die Benutzerliste wäre
  // die falsche Quelle: die darf nur die Verwaltung lesen, das eigene
  // Zertifikat geht aber jeden Prüfer etwas an.
  const { konto, aktualisieren } = useKonto()
  const speichern = useEigenesAendern()

  const [name, setzeName] = useState('')
  const [telefon, setzeTelefon] = useState('')
  const [zertifikat, setzeZertifikat] = useState('')
  const [gespeichert, setzeGespeichert] = useState(false)

  // Erst wenn die Daten da sind, ins Formular übernehmen. Ohne das stünde im
  // Feld ein leerer Wert, den ein Speichern übernehmen würde.
  useEffect(() => {
    setzeName(konto?.name ?? '')
    setzeTelefon(konto?.telefon ?? '')
    setzeZertifikat(konto?.zertifikat ?? '')
  }, [konto])

  return (
    <form
      className="karte space-y-3"
      onSubmit={(e) => {
        e.preventDefault()
        setzeGespeichert(false)
        speichern.mutate(
          {
            name,
            // Leeres Feld heißt: Angabe löschen, nicht „unverändert".
            telefon: telefon.trim() || null,
            zertifikat: zertifikat.trim() || null,
          },
          {
            onSuccess: () => {
              setzeGespeichert(true)
              // Der Name steht in der Kopfzeile und im Bericht – die Kopie im
              // Kontokontext muss mitziehen.
              void aktualisieren()
            },
          },
        )
      }}
    >
      <h2 className="text-xl font-bold">Angaben zur Person</h2>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <label htmlFor="eigen-name">Name</label>
          <input
            id="eigen-name"
            value={name}
            onChange={(e) => setzeName(e.target.value)}
            required
            maxLength={120}
          />
        </div>
        <div>
          <label htmlFor="eigen-telefon">Telefon</label>
          <input
            id="eigen-telefon"
            type="tel"
            value={telefon}
            onChange={(e) => setzeTelefon(e.target.value)}
            placeholder="0170 1234567"
            maxLength={50}
          />
        </div>
        <div>
          <label htmlFor="eigen-zertifikat">Zertifikat</label>
          <input
            id="eigen-zertifikat"
            value={zertifikat}
            onChange={(e) => setzeZertifikat(e.target.value)}
            placeholder="QSP-AGIS-00124"
            maxLength={60}
          />
          <p className="mt-1 text-base text-slate-600">
            Erscheint im Bericht unter der Unterschrift.
          </p>
        </div>
      </div>

      <Fehlerhinweis fehler={speichern.error} />

      <div className="flex items-center gap-3">
        <button type="submit" className="knopf-haupt" disabled={speichern.isPending}>
          {speichern.isPending ? 'Speichert …' : 'Speichern'}
        </button>
        {gespeichert && (
          <span role="status" className="marke bg-stufe-ob-grund text-stufe-ob">
            Gespeichert
          </span>
        )}
      </div>
    </form>
  )
}

function Passwortwechsel() {
  const aendern = usePasswortAendern()
  const [alt, setzeAlt] = useState('')
  const [neu, setzeNeu] = useState('')
  const [wiederholung, setzeWiederholung] = useState('')
  const [fertig, setzeFertig] = useState(false)

  // Die Wiederholung prüft die Oberfläche selbst: der Server kennt nur ein
  // Passwortfeld, und ein Tippfehler wäre sonst erst beim nächsten Anmelden
  // aufgefallen.
  const passtNicht = wiederholung.length > 0 && neu !== wiederholung
  const zuKurz = neu.length > 0 && neu.length < 12

  return (
    <form
      className="karte space-y-3"
      onSubmit={(e) => {
        e.preventDefault()
        setzeFertig(false)
        aendern.mutate(
          { altes_passwort: alt, neues_passwort: neu },
          {
            onSuccess: () => {
              setzeAlt('')
              setzeNeu('')
              setzeWiederholung('')
              setzeFertig(true)
            },
          },
        )
      }}
    >
      <h2 className="text-xl font-bold">Passwort ändern</h2>

      <div>
        <label htmlFor="pw-alt">Bisheriges Passwort</label>
        <input
          id="pw-alt"
          type="password"
          autoComplete="current-password"
          value={alt}
          onChange={(e) => setzeAlt(e.target.value)}
          required
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="pw-neu">Neues Passwort</label>
          <input
            id="pw-neu"
            type="password"
            autoComplete="new-password"
            value={neu}
            onChange={(e) => setzeNeu(e.target.value)}
            required
            minLength={12}
            aria-describedby="pw-hinweis"
          />
          <p id="pw-hinweis" className="mt-1 text-base text-slate-600">
            Mindestens 12 Zeichen. Ein Satz ist leichter zu merken als ein Zeichensalat.
          </p>
        </div>
        <div>
          <label htmlFor="pw-wdh">Wiederholung</label>
          <input
            id="pw-wdh"
            type="password"
            autoComplete="new-password"
            value={wiederholung}
            onChange={(e) => setzeWiederholung(e.target.value)}
            required
            aria-invalid={passtNicht}
          />
          {passtNicht && (
            <p role="alert" className="mt-1 text-base font-semibold text-stufe-3">
              Die beiden Eingaben sind nicht gleich.
            </p>
          )}
        </div>
      </div>

      <Fehlerhinweis fehler={aendern.error} />

      <div className="flex items-center gap-3">
        <button
          type="submit"
          className="knopf-haupt"
          disabled={aendern.isPending || passtNicht || zuKurz || !alt || !neu}
        >
          {aendern.isPending ? 'Ändert …' : 'Passwort ändern'}
        </button>
        {fertig && (
          <span role="status" className="marke bg-stufe-ob-grund text-stufe-ob">
            Passwort geändert
          </span>
        )}
      </div>
    </form>
  )
}


/**
 * Die hinterlegte Unterschrift.
 *
 * Einmal zeichnen, beim Abschluss jeder Prüfung mit einem Griff anfügen.
 * Ohne das müsste sie auf jedem Platz neu gezeichnet werden – mit
 * Handschuhen, im Regen, und jedes Mal ein bisschen anders.
 *
 * Die Prüfung bekommt beim Abschluss eine Kopie. Was hier später geändert
 * wird, verändert deshalb keinen Bericht, der schon geschrieben ist.
 */
function Unterschriftsablage() {
  const { konto, aktualisieren } = useKonto()
  const speichern = useEigenesAendern()
  const [zeichnen, setzeZeichnen] = useState(false)
  const [entwurf, setzeEntwurf] = useState<string | null>(null)
  const [gespeichert, setzeGespeichert] = useState(false)

  function sichern(bild: string | null) {
    setzeGespeichert(false)
    speichern.mutate(
      { unterschrift: bild },
      {
        onSuccess: () => {
          setzeZeichnen(false)
          setzeEntwurf(null)
          setzeGespeichert(true)
          void aktualisieren()
        },
      },
    )
  }

  return (
    <div className="karte space-y-3">
      <h2 className="text-xl font-bold">Meine Unterschrift</h2>
      <p className="text-base text-slate-700">
        Sie erscheint im Bericht auf der Unterschriftslinie. Beim Abschluss
        einer Prüfung fügen Sie sie mit einem Griff an.
      </p>

      {zeichnen ? (
        <>
          <Unterschriftfeld
            vorhanden={konto?.unterschrift}
            beimZeichnen={setzeEntwurf}
          />
          <Fehlerhinweis fehler={speichern.error} />
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="knopf-haupt"
              disabled={!entwurf || speichern.isPending}
              onClick={() => entwurf && sichern(entwurf)}
            >
              {speichern.isPending ? 'Speichert …' : 'Unterschrift speichern'}
            </button>
            <button
              type="button"
              className="knopf-still"
              onClick={() => {
                setzeZeichnen(false)
                setzeEntwurf(null)
                speichern.reset()
              }}
            >
              Abbrechen
            </button>
          </div>
        </>
      ) : (
        <>
          {konto?.unterschrift ? (
            <img
              src={konto.unterschrift}
              alt="Ihre hinterlegte Unterschrift"
              className="max-h-24 rounded-feld border-2 border-slate-200 bg-white p-2"
            />
          ) : (
            <p className="text-base text-slate-600">
              Noch keine Unterschrift hinterlegt. Berichte erscheinen dann mit
              leerer Unterschriftslinie zum Unterschreiben von Hand.
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="knopf-zweit"
              onClick={() => setzeZeichnen(true)}
            >
              {konto?.unterschrift ? 'Neu zeichnen' : 'Jetzt unterschreiben'}
            </button>
            {konto?.unterschrift && (
              <button
                type="button"
                className="knopf-still"
                onClick={() => {
                  if (window.confirm('Hinterlegte Unterschrift entfernen?')) {
                    sichern(null)
                  }
                }}
              >
                Entfernen
              </button>
            )}
            {gespeichert && (
              <span role="status" className="marke bg-stufe-ob-grund text-stufe-ob">
                Gespeichert
              </span>
            )}
          </div>
          <Fehlerhinweis fehler={speichern.error} />
        </>
      )}
    </div>
  )
}


/**
 * Prüftermine in den eigenen Outlook-Kalender schreiben.
 *
 * Nur in eine Richtung: aus SPÜR nach Outlook. Ein Prüftermin trägt eine
 * Frist, keine Uhrzeit – sie folgt aus dem Prüfintervall. Ließe man den
 * Kalender zurückschreiben, verschöbe ein Griff im Kalender eine Frist, an
 * der die Verkehrssicherungspflicht hängt. Deshalb steht hier ausdrücklich,
 * in welche Richtung es geht.
 */
function Kalenderabgleich() {
  const { konto, aktualisieren } = useKonto()
  const speichern = useEigenesAendern()
  const abgleichen = useKalenderabgleich()
  const an = konto?.kalender_sync ?? false

  function umschalten(neu: boolean) {
    abgleichen.reset()
    speichern.mutate(
      { kalender_sync: neu },
      {
        onSuccess: async () => {
          await aktualisieren()
          // Gleich befüllen, sonst bleibt der Kalender bis zum nächtlichen
          // Lauf leer und der Schalter sieht wirkungslos aus.
          if (neu) abgleichen.mutate()
        },
      },
    )
  }

  return (
    <div className="karte space-y-3">
      <h2 className="text-xl font-bold">Prüftermine im Kalender</h2>
      <p className="text-base text-slate-700">
        Auf Wunsch schreibt SPÜR Ihre offenen und eingeplanten Prüfungen sowie
        zugesagte Terminanfragen als ganztägige Termine in einen eigenen
        Outlook-Kalender „SPÜR Prüftermine“. Der Abgleich läuft nächtlich und
        geht nur in eine Richtung: Änderungen im Kalender wirken nicht zurück,
        damit eine Frist nicht versehentlich durch Verschieben im Kalender
        verrutscht.
      </p>

      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          className="mt-1 h-5 w-5"
          checked={an}
          disabled={speichern.isPending}
          onChange={(e) => umschalten(e.target.checked)}
        />
        <span className="text-base">
          Prüftermine in meinen Outlook-Kalender schreiben
          <span className="block text-sm text-slate-600">
            Beim Ausschalten werden die geschriebenen Termine wieder entfernt.
          </span>
        </span>
      </label>

      {an && (
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="knopf-zweit"
            disabled={abgleichen.isPending}
            onClick={() => abgleichen.mutate()}
          >
            {abgleichen.isPending ? 'Gleicht ab …' : 'Jetzt abgleichen'}
          </button>
          {abgleichen.data && (
            <span role="status" className="text-base text-slate-700">
              {abgleichen.data.angelegt} neu, {abgleichen.data.geaendert}{' '}
              geändert, {abgleichen.data.entfernt} entfernt,{' '}
              {abgleichen.data.unveraendert} unverändert.
            </span>
          )}
        </div>
      )}

      <Fehlerhinweis fehler={speichern.error ?? abgleichen.error} />
    </div>
  )
}
